/**
 * Collection Hero: Category Tabs
 * Handles tab navigation and active state management for collection hero category tabs
 * @param {object} opts
 */
export default function collectionHeroCategoryTabs(opts) {
  const config = {
    dom: {
      container: "collection-hero-category-tabs",
      tab: "collection-hero-category-tabs__tab",
      tabsWrapper: "collection-hero-category-tabs__tabs",
      activeTab: "collection-hero-category-tabs__tab--active"
    },
    cls: {
      active: "collection-hero-category-tabs__tab--active"
    },
    scroll: {
      behavior: "smooth",
      block: "nearest",
      inline: "center"
    }
  };

  const c = __.extend(config, opts);

  // Global elements
  const $containers = document.querySelectorAll(`.${c.dom.container}`);

  /**
   * Initialize a single container
   * @param {HTMLElement} $container
   * @param {boolean} isInitialLoad
   */
  function initContainer($container, isInitialLoad = false) {
    const $activeTab = $container.querySelector(`.${c.dom.activeTab}`);
    const $tabsWrapper = $container.querySelector(`.${c.dom.tabsWrapper}--scrollable`);

    const $tabs = $container.querySelector(`.${c.dom.tabsWrapper}`);
    const $allTabs = $container.querySelectorAll(`.${c.dom.tab}`);

    // Handle tab width calculations
    if ($tabs && $allTabs.length) {
      const tabCount = $allTabs.length;
      
      // Only adjust widths for 5+ tabs on desktop only
      if (tabCount >= 5 && window.innerWidth >= 1024) {
        const tabWidth = `${100 / Number(tabCount)}%`;
        
        $allTabs.forEach(($tab, index) => {
          $tab.style.width = tabWidth;
          
          // First tab gets 1px extra to prevent wrapping
          if (index === 0) {
            $tab.style.width = `calc(${tabWidth} + 1px)`;
          }
        });
      }
    }
    if (isInitialLoad && $activeTab && $tabsWrapper) {
      $activeTab.scrollIntoView(c.scroll);
    }
  }

  /**
   * Create scroll arrow button
   * @param {string} dir - 'prev' or 'next'
   * @returns {HTMLElement}
   */
  function createScrollButton(dir) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `collection-hero-category-tabs__scroll-btn collection-hero-category-tabs__scroll-btn--${dir}`;
    btn.setAttribute("aria-label", dir === "prev" ? "Scroll left" : "Scroll right");
    btn.innerHTML = dir === "prev"
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    return btn;
  }

  /**
   * Update scroll button disabled state
   * @param {HTMLElement} scroller
   * @param {HTMLElement} prevBtn
   * @param {HTMLElement} nextBtn
   */
  function updateScrollButtons(scroller, prevBtn, nextBtn) {
    const max = scroller.scrollWidth - scroller.clientWidth;
    if (max <= 0) {
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }
    const x = Math.round(scroller.scrollLeft);
    prevBtn.disabled = x <= 0;
    nextBtn.disabled = x >= max - 1;
  }

  /**
   * Prevent iOS Safari elastic (rubber-band) overscroll from revealing blank
   * space past the first/last tab. `overscroll-behavior` only stops scroll
   * chaining to the page on iOS — it does NOT suppress an inner scroller's own
   * bounce — so we clamp scrollLeft back within bounds as the bounce occurs.
   * @param {HTMLElement} scroller
   */
  function preventElasticOverscroll(scroller) {
    scroller.addEventListener("scroll", () => {
      const max = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft < 0) {
        scroller.scrollLeft = 0;
      } else if (scroller.scrollLeft > max) {
        scroller.scrollLeft = max;
      }
    }, { passive: true });
  }

  /**
   * Attach scroll arrows to scrollable tabs container
   * @param {HTMLElement} $container
   */
  function attachScrollArrows($container) {
    const $tabsWrapper = $container.querySelector(`.${c.dom.tabsWrapper}--scrollable`);
    if (!$tabsWrapper || $tabsWrapper.dataset.scrollArrowsAttached === "1") {
      return;
    }
    
    $tabsWrapper.dataset.scrollArrowsAttached = "1";
    
    // Find or create wrapper for positioning arrows
    const $tabsContainer = $tabsWrapper.closest(".collection-hero-category-tabs__tabs-wrapper") || $tabsWrapper.parentElement;
    if (!$tabsContainer) {
      return;
    }
    
    $tabsContainer.classList.add("collection-hero-category-tabs__tabs-wrapper--has-scroll-arrows");
    
    const prevBtn = createScrollButton("prev");
    const nextBtn = createScrollButton("next");
    $tabsContainer.appendChild(prevBtn);
    $tabsContainer.appendChild(nextBtn);
    
    const step = () => Math.max(120, Math.floor($tabsWrapper.clientWidth * 0.9));
    
    prevBtn.addEventListener("click", () => {
      $tabsWrapper.scrollBy({ left: -step(), behavior: "smooth" });
    });
    
    nextBtn.addEventListener("click", () => {
      $tabsWrapper.scrollBy({ left: step(), behavior: "smooth" });
    });
    
    preventElasticOverscroll($tabsWrapper);

    const onUpdate = () => updateScrollButtons($tabsWrapper, prevBtn, nextBtn);
    $tabsWrapper.addEventListener("scroll", onUpdate, { passive: true });
    window.addEventListener("resize", onUpdate);
    
    // Initial update after layout settles
    setTimeout(onUpdate, 50);
  }

  /**
   * Init
   */
  function init() {
    if (!$containers?.length) {
      return;
    }
    
    [...$containers].forEach(($container) => {
      initContainer($container, true);
      attachScrollArrows($container);
    });

    // Handle window resize
    let resizeTimeout;
    window.addEventListener("resize", () => {
      // Debounce resize handler
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        [...$containers].forEach(($container) => {
          initContainer($container, false);
        });
      }, 250);
    });
  }

  return {
    init
  };
}

// Auto-initialize on DOM ready
document.addEventListener("DOMContentLoaded", function() {
  collectionHeroCategoryTabs().init();
});

